import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  CircularProgress,
  Autocomplete,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FlagIcon from '@mui/icons-material/Flag';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { STATUSES } from '../../api/endpoints';
import { toast } from 'react-toastify';
import { buildStatusFormData, STATUS_MODEL_OPTIONS } from './statusApiHelpers';
import { STATUSES_LIST_PATH } from '../../navigations/path';
import { clearStatusesCache } from '../../utils/lookupSessionCache';

const CreateStatus = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [model, setModel] = useState('');
  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [icon, setIcon] = useState('');
  const [errors, setErrors] = useState({});

  const validate = () => {
    const next = {};
    if (!model.trim()) next.model = 'Model is required';
    if (!name.trim()) next.name = 'Name is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const fd = buildStatusFormData({
        model: model.trim(),
        name: name.trim(),
        color: color.trim(),
        icon: icon.trim(),
      });
      const { data } = await api.post(STATUSES.LIST, fd);
      if (data?.success === false) {
        toast.error(data?.message || 'Failed to create status');
        return;
      }
      clearStatusesCache(model.trim());
      toast.success(data?.message || 'Status created successfully');
      navigate(STATUSES_LIST_PATH);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create status');
    } finally {
      setSubmitting(false);
    }
  };

  const clearError = (field) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(STATUSES_LIST_PATH)}
          sx={{ mb: 2 }}
        >
          Back to statuses
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          Create status
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={STATUS_MODEL_OPTIONS}
                  value={model}
                  onChange={(_, v) => {
                    setModel(v ?? '');
                    clearError('model');
                  }}
                  onInputChange={(_, v) => {
                    setModel(v);
                    clearError('model');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Model *"
                      placeholder="e.g. payment, Lead, Equipment"
                      error={!!errors.model}
                      helperText={errors.model || 'Entity this status applies to'}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name *"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    clearError('name');
                  }}
                  error={!!errors.name}
                  helperText={errors.name}
                  fullWidth
                  placeholder="e.g. Active, Pending"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  fullWidth
                  placeholder="e.g. black, #2196f3, success"
                  helperText="Display color for badges (CSS color name or hex)"
                />
                {color.trim() && (
                  <Chip
                    label={color}
                    size="small"
                    sx={{ mt: 1, bgcolor: color, color: 'common.white' }}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Icon"
                  value={icon}
                  onChange={(e) => setIcon(e.target.value)}
                  fullWidth
                  placeholder="e.g. fa-flag"
                  helperText="Icon class (e.g. Font Awesome)"
                  InputProps={{
                    startAdornment: icon ? (
                      <FlagIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />
                    ) : undefined,
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => navigate(STATUSES_LIST_PATH)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? <CircularProgress size={22} color="inherit" /> : 'Create status'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateStatus;
